import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, ViewChild, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';

import { COMMON_STANDALONE_IMPORTS } from '@shared/common-imports';
import { FormErrorComponent } from '@shared/components';
import { RequiredLabelDirective } from '@shared/directives';
import { ToastsService, AuthService } from '@shared/services';
import { FormGenerator, FormGroupExtension } from '@shared/utils';

import { AuthCardComponent } from '../auth-card/auth-card.component';

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorInstance {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}

type BarcodeDetectorConstructor = new () => BarcodeDetectorInstance;

type BadgeCodeReader = (video: HTMLVideoElement) => Promise<string | undefined>;

const CAMERA_SCAN_INTERVAL_MS = 300;

@Component({
  selector: 'app-company-login',
  templateUrl: './company-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ...COMMON_STANDALONE_IMPORTS,
    TranslateModule,
    FormErrorComponent,
    ButtonModule,
    RequiredLabelDirective,
    AuthCardComponent,
  ],
})
export class CompanyLoginComponent implements OnInit {
  private cameraStream?: MediaStream;
  private scanTimer?: ReturnType<typeof setInterval>;

  readonly loader = 'companyLoginComponentLoader';
  readonly companyId = this.route.snapshot.paramMap.get('companyId') ?? '';
  readonly editForm = FormGenerator.initBadgeLoginForm(this.companyId);
  // INFO (Denis) - only the camera is required: browsers without the native BarcodeDetector (e.g. Firefox, desktop Chrome on Windows/Linux) fall back to ZXing.
  readonly isCameraSupported = !!navigator.mediaDevices?.getUserMedia;

  readonly isCameraActive = signal(false);

  @ViewChild('badgeCodeInput') badgeCodeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('cameraVideo') cameraVideo?: ElementRef<HTMLVideoElement>;

  constructor(
    private translate: TranslateService,
    private toastsService: ToastsService,
    private authService: AuthService,
    private route: ActivatedRoute,
    destroyRef: DestroyRef
  ) {
    destroyRef.onDestroy(() => this.stopCamera());
  }

  ngOnInit(): void {
    if (this.authService.isUserLoggedIn()) {
      this.authService.navigateByUserRole();
      return;
    }

    setTimeout(() => this.badgeCodeInput?.nativeElement.focus());
  }

  onSubmit(): void {
    if (FormGroupExtension.hasAnyValidationError(this.editForm)) {
      FormGroupExtension.markFormAsDirtyAndTouched(this.editForm);
      return;
    }

    const { companyId, badgeCode } = this.editForm.getRawValue();
    this.stopCamera();
    this.authService.loginByBadgeCode(this.loader, companyId, badgeCode.trim()).subscribe({
      next: (response) => {
        if (response?.user) {
          this.toastsService.showSuccess(
            this.translate.instant('auth.login.welcomeBack') + ` ${response.user.fullName ?? ''}!`
          );
        }
      },
      error: (error) => {
        this.editForm.controls.badgeCode.reset();
        this.badgeCodeInput?.nativeElement.focus();
        this.toastsService.showError(error);
      },
    });
  }

  async onToggleCamera(): Promise<void> {
    if (this.isCameraActive()) {
      this.stopCamera();
      return;
    }

    try {
      this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    } catch {
      this.toastsService.showError(this.translate.instant('auth.companyLogin.cameraNotAvailable'));
      return;
    }

    this.isCameraActive.set(true);
    setTimeout(() => this.startScanning());
  }

  private async startScanning(): Promise<void> {
    const video = this.cameraVideo?.nativeElement;
    if (!video || !this.cameraStream) {
      this.stopCamera();
      return;
    }

    video.srcObject = this.cameraStream;
    void video.play();

    const readBadgeCode = await this.createBadgeCodeReader();
    if (!this.isCameraActive()) {
      return;
    }

    this.scanTimer = setInterval(async () => {
      if (video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
        return;
      }

      const code = await readBadgeCode(video);
      if (code && this.isCameraActive()) {
        this.editForm.controls.badgeCode.setValue(code);
        this.onSubmit();
      }
    }, CAMERA_SCAN_INTERVAL_MS);
  }

  private stopCamera(): void {
    clearInterval(this.scanTimer);
    this.cameraStream?.getTracks().forEach((track) => track.stop());
    this.cameraStream = null;
    this.isCameraActive.set(false);
  }

  private async createBadgeCodeReader(): Promise<BadgeCodeReader> {
    const BarcodeDetector = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
    if (BarcodeDetector) {
      const detector = new BarcodeDetector();
      return async (video) => {
        const codes = await detector.detect(video).catch(() => [] as DetectedBarcode[]);
        return codes.find((x) => x.rawValue?.trim())?.rawValue.trim();
      };
    }

    // INFO (Denis) - loaded on demand, so the login page bundle stays small for browsers with a native detector.
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const reader = new BrowserMultiFormatReader();
    return async (video) => {
      try {
        return reader.decode(video).getText().trim() || undefined;
      } catch {
        // ZXing throws when the frame contains no barcode.
        return undefined;
      }
    };
  }
}
